import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import JsonLd from "@/components/JsonLd";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";
import { getYvelinesCityTemplate } from "@/data/yvelinesCityTemplates";

const SITE_URL = "https://cartonmove.fr";

const FEATURED_SLUGS = [
  "carton-standard-renforce",
  "carton-a-livres",
  "carton-12-bouteilles",
  "carton-75-verres",
];

const buildCityServiceSchema = (city) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Vente de cartons et fournitures de déménagement",
  name: `Cartons de déménagement à ${city.name}`,
  description: `Vente en ligne de cartons et fournitures de déménagement livrés à ${city.name} (${city.deptCode}, ${city.deptName}). Propre flotte de livraison en Île-de-France.`,
  provider: {
    "@type": "Organization",
    name: "cartonmove",
    url: SITE_URL,
  },
  areaServed: [
    {
      "@type": "City",
      name: city.name,
      containedInPlace: {
        "@type": "AdministrativeArea",
        name: `${city.deptName} (${city.deptCode})`,
      },
    },
  ],
  url: `${SITE_URL}/carton-demenagement-${city.slug}`,
});

const buildH1 = (city) => {
  const titles = {
    versailles: `Vente et livraison de cartons de déménagement à ${city.name} pour particuliers et professionnels`,
    "le-chesnay-rocquencourt": `Votre boutique de vente de cartons de déménagement ${city.aCity}`,
    "velizy-villacoublay": `Cartons et fournitures de déménagement ${city.aCity}`,
    "saint-cyr-lecole": `Cartons de déménagement ${city.aCity}`,
    viroflay: `Vente et livraison de cartons de déménagement ${city.aCity}`,
    buc: `Vos cartons de déménagement ${city.aCity} : qualité haut de gamme`,
    guyancourt: `Vente de cartons de déménagement ${city.aCity} : le choix de la tranquillité pour emballer vos affaires`,
    "montigny-le-bretonneux": `Vente et livraison de cartons de déménagement ${city.aCity}`,
    trappes: `Cartons de déménagement ${city.aCity} : pour entreprises et particuliers`,
    chateaufort: `Cartons de déménagement ${city.aCity} : qualité professionnelle`,
    "bois-darcy": `Vos fournitures de déménagement sur-mesure ${city.aCity}`,
    "fontenay-le-fleury": `Cartons de déménagement ${city.aCity} conçus pour faciliter le transport de vos biens`,
    "les-clayes-sous-bois": `Cartons de déménagement ${city.aCity} : le choix de la performance pour vos préparatifs`,
    "jouy-en-josas": `Cartons de déménagement ${city.aCity} : sécurisez vos biens dans un cadre d'exception`,
    "les-loges-en-josas": `Cartons de déménagement ${city.aCity} : protégez vos biens au cœur de la vallée de la Bièvre`,
    "toussus-le-noble": `Fournitures de déménagement ${city.aCity} : conçues pour faciliter le transport de vos biens`,
    plaisir: `Vente de cartons de déménagement ${city.aCity} : pour particuliers et entreprises`,
    villepreux: `Vente et livraison de cartons de déménagement ${city.aCity}`,
    chavenay: `Cartons de déménagement ${city.aCity} : un matériel d'emballage irréprochable`,
    rennemoulin: `Vente et livraison de cartons de déménagement ${city.aCity}`,
    "noisy-le-roi": `Vos cartons de déménagement ${city.aCity}`,
    bailly: `Votre boutique de cartons de déménagement avec livraison à domicile ${city.aCity}`,
    "saint-nom-la-breteche": `Cartons de déménagement ${city.aCity} : qualité premium`,
    davron: `Cartons de déménagement ${city.aCity} : préservez la sérénité de votre projet de vie`,
    poissy: `Votre boutique de cartons de déménagement ${city.aCity} : départ d'un déménagement serein et sécurisé`,
    "carrieres-sous-poissy": `Vente et livraison de cartons de déménagement ${city.aCity}`,
    orgeval: `Boutique de cartons de déménagement ${city.aCity} : qualité premium`,
    chambourcy: `Cartons de déménagement ${city.aCity} : pour particuliers et entreprises`,
    "villennes-sur-seine": `Fournitures de déménagement ${city.aCity} : cartons, emballages et protections`,
    "verneuil-sur-seine": `Vente et livraison de cartons de déménagement ${city.aCity}`,
    vernouillet: `Cartons de déménagement ${city.aCity} : réussir votre transition en toute sérénité`,
    morainvilliers: `Cartons de déménagement ${city.aCity} : emballez vos biens en toute sérénité`,
    "maisons-laffitte": `Vente et livraison de cartons de déménagement ${city.aCity} : pour particuliers et entreprises`,
    "le-mesnil-le-roi": `Cartons de déménagement ${city.aCity} : le choix de la tranquillité pour emballer vos affaires`,
    acheres: `Vente et livraison de cartons de déménagement ${city.aCity}`,
    "conflans-sainte-honorine": `Cartons de déménagement ${city.aCity} : pensés pour protéger ce qui compte le plus`,
    andresy: `Vente de cartons de déménagement ${city.aCity} : emballez vos projets en toute sérénité`,
    "chanteloup-les-vignes": `Cartons de déménagement ${city.aCity} : fournitures haut de gamme`,
    maurecourt: `Cartons de déménagement ${city.aCity} : facilitez vos préparatifs avec du matériel de qualité`,
    "triel-sur-seine": `Cartons de déménagement ${city.aCity} : emballez en toute sérénité`,
    chatou: `Cartons de déménagement ${city.aCity} : vente et livraison expresse`,
    "le-vesinet": `Cartons de déménagement ${city.aCity} : l'exigence du matériel professionnel pour vos transferts`,
    "croissy-sur-seine": `Cartons de déménagement ${city.aCity} : votre trajet entre de bonnes mains`,
    bougival: `Fournitures de déménagement ${city.aCity} : emballez vos biens en toute confiance`,
    "la-celle-saint-cloud": `Cartons de déménagement ${city.aCity} : l'excellence de l'emballage pour un départ serein`,
    louveciennes: `Vos cartons de déménagement ${city.aCity} : qualité haut de gamme`,
    "marly-le-roi": `Cartons de déménagement ${city.aCity} : vente et livraison`,
    "le-port-marly": `Vente et livraison de cartons de déménagement ${city.aCity}`,
    houilles: `Vente et livraison de cartons de déménagement ${city.aCity} : pour entreprises et particuliers`,
    "carrieres-sur-seine": `Cartons de déménagement ${city.aCity} : emballez vos biens en toute confiance`,
    montesson: `Cartons de déménagement ${city.aCity} : qualité premium`,
    "saint-germain-en-laye": `Boutique de vente et livraison de cartons de déménagement ${city.aCity}`,
    "le-pecq": `Cartons de déménagement ${city.aCity} : emballages et protections`,
    "mareil-marly": `Cartons de déménagement ${city.aCity} : particuliers et entreprises`,
    "letang-la-ville": `Cartons de déménagement ${city.aCity} : protégez vos biens dans un écrin de verdure`,
    feucherolles: `Vente et livraison de cartons de déménagement ${city.aCity}`,
    sartrouville: `Vente et livraison de cartons de déménagement ${city.aCity} pour particuliers et professionnels`,
    aigremont: `Vente et livraison de cartons de déménagement ${city.aCity} : qualité pro`,
    andelu: `Cartons de déménagement ${city.aCity} : préparer sereinement votre déménagement`,
    bazemont: `Cartons de déménagement ${city.aCity} : vente et livraison`,
    crespieres: `Cartons de déménagement ${city.aCity} : vente et livraison`,
    herbeville: `Vente et livraison de cartons de déménagement ${city.aCity} : particuliers et entreprises`,
    "les-alluets-le-roi": `Cartons de déménagement ${city.aCity} : le choix de la sérénité pour votre déménagement`,
    "mareil-sur-mauldre": `Cartons de déménagement ${city.aCity} : une organisation simplifiée grâce à des fournitures adaptées`,
  };
  return titles[city.templateId] || `Cartons de déménagement ${city.aCity || `à ${city.name}`}`;
};

const Intro = ({ city }) => {
  if (city.templateId === "versailles") {
    return (
      <>
        Planifier un changement d&apos;adresse à {city.name}, que vous
        quittiez un appartement de maître dans le quartier historique de
        Saint-Louis, une demeure bourgeoise à Notre-Dame, un pavillon
        arboré à Porchefontaine ou que vous transfériez des bureaux près
        de l&apos;avenue de Paris, s&apos;apparente à une véritable œuvre
        logistique. Dans une ville bâtie sur l&apos;ordre et la symétrie,
        chaque étape de votre déménagement exige une rigueur absolue. Vos
        collections de livres, vos pièces d&apos;art, votre mobilier de
        valeur et vos souvenirs précieux ne peuvent être confiés au
        hasard. Utiliser des cartons industriels calibrés et renforcés,
        plutôt que des emballages de fortune déformables, est le seul
        secret pour ériger un rempart inviolable autour de votre
        patrimoine et garantir une installation souveraine dans votre
        nouvelle demeure.
      </>
    );
  }
  if (city.templateId === "le-chesnay-rocquencourt") {
    return (
      <>
        Réussir son changement de domicile {city.aCity}, que vous habitiez
        à proximité immédiate du centre commercial Westfield Parly 2, dans
        le secteur recherché de Glatigny, ou dans les quartiers paisibles
        de Rocquencourt, exige une anticipation sans faille.
        L&apos;effervescence de cette commune limitrophe de Versailles
        impose de s&apos;équiper avec un matériel de transport fiable pour
        éviter les contretemps le jour J.
        <br />
        <br />
        En misant sur notre catalogue d&apos;emballages haut de gamme,
        vous faites le choix de la sérénité technologique. Finies les
        boîtes de récupération déformées ou humides qui menacent de céder
        au moindre levage. Nos fournitures rigides offrent une armure
        protectrice à tout votre patrimoine, garantissant un chargement
        fluide, stable et parfaitement sécurisé dans le camion de
        déménagement.
      </>
    );
  }
  if (city.templateId === "velizy-villacoublay") {
    return (
      <>
        Changer d&apos;adresse {city.aCity}, que vous vous installiez dans
        un appartement moderne du quartier connecté de Louvois, une zone
        résidentielle proche de la forêt, ou que vous pilotiez le
        transfert stratégique d&apos;un siège social au cœur de la zone
        d&apos;emplois de Vélizy-Inovel Parc, exige une rigueur absolue.
        Dans une commune caractérisée par son dynamisme technologique et
        ses infrastructures majeures (A86, N118), un déménagement ne
        s&apos;improvise pas. La réussite de votre transition repose sur
        la qualité de vos contenants : abandonner les emballages de
        fortune pour du matériel calibré est le seul moyen de garantir
        que vos serveurs informatiques, vos écrans, vos dossiers précieux
        et vos effets personnels franchissent cette étape sans la
        moindre égratignure.
      </>
    );
  }
  if (city.templateId === "saint-cyr-lecole") {
    return (
      <>
        Planifier un changement d&apos;adresse {city.aCity}, que vous vous
        installiez dans un appartement moderne du quartier de la Plaine de
        Vie, une maison près de l&apos;aérodrome historique, ou que vous
        transfériez des bureaux à proximité de l&apos;École Militaire,
        demande une organisation rigoureuse. Pour réussir votre transition
        sans stress, la qualité de vos emballages est votre premier atout.
        Oubliez les cartons de récupération minces qui se déchirent sous
        le poids. En choisissant nos gammes de cartonnerie neuve et
        renforcée de qualité industrielle, vous garantissez à vos livres,
        vaisselles et équipements une stabilité parfaite et une protection
        totale contre les vibrations du transport.
      </>
    );
  }
  if (city.templateId === "viroflay") {
    return (
      <>
        Planifier un changement de résidence {city.aCity}, que vous
        transitiez par le quartier recherché de la Rive-Droite, les
        secteurs paisibles de la Rive-Gauche ou les zones limitrophes de la
        forêt de Fausses-Reposes, exige une organisation d&apos;une
        précision absolue. Dans une commune caractérisée par son élégance
        résidentielle et sa connectivité immédiate avec Versailles et
        Paris, la sécurité de vos biens ne tolère aucun compromis.
        Utiliser une cartonnerie professionnelle robuste, neuve et normée
        est le seul moyen de garantir que vos objets précieux, votre
        mobilier et vos archives traversent la manutention urbaine en
        toute intégrité.
      </>
    );
  }
  if (city.templateId === "buc") {
    return (
      <>
        Vous orchestrez un projet de déménagement {city.aCity}, au cœur de
        la vallée de la Bièvre ou à proximité du site aéronautique
        historique ? Pour que cette nouvelle page de votre vie
        s&apos;écrive sans encombre, l&apos;emballage de vos affaires est
        l&apos;étape stratégique à ne pas négliger. Quitter ou
        s&apos;installer dans une nouvelle habitation demande des
        contenants fiables, capables de supporter les manipulations et le
        transport. Nos gammes de fournitures professionnelles sont
        spécialement sélectionnées pour offrir une protection maximale à
        vos biens, balayant définitivement le stress des cartons qui
        cèdent ou se déforment pendant le trajet.
      </>
    );
  }
  if (city.templateId === "montigny-le-bretonneux") {
    return (
      <>
        Planifier un changement d&apos;adresse {city.aCity}, que vous
        emménagiez dans une maison familiale dans le quartier résidentiel
        du Plan de l&apos;Église, un appartement moderne aux Prés ou à la
        Sourderie, ou que vous transfériez des bureaux près de la gare de
        Saint-Quentin, demande une logistique rigoureuse. Dans une ville
        aussi dynamique et structurée, le secret d&apos;un déménagement
        sans stress réside dans la qualité de votre matériel
        d&apos;emballage. Nos fournitures de qualité industrielle neuves
        et renforcées protègent vos équipements multimédias, vos livres
        et vos objets fragiles contre les vibrations et les chocs du
        transport, garantissant leur arrivée intacte.
      </>
    );
  }
  if (city.templateId === "trappes") {
    return (
      <>
        Voyager depuis ou vers Trappes, que vous quittiez un pavillon
        niché près du calme de la base de loisirs, un appartement
        contemporain du secteur de l&apos;Aérostat, ou que vous pilotiez
        le transfert d&apos;une entreprise à proximité de la RN10 ou de
        la gare ferroviaire, impose un défi logistique de taille. Une
        fois chargé dans le camion, votre mobilier va affronter les
        accélérations, les freinages et les ronds-points des
        infrastructures urbaines des Yvelines. Pour que vos biens restent
        immobiles et intacts, le choix de vos contenants est crucial.
        L&apos;utilisation de cartons industriels rigides et calibrés,
        plutôt que d&apos;emballages de fortune déformables, est la seule
        méthode éprouvée pour ériger un rempart solide autour de votre
        patrimoine et garantir une arrivée à destination en toute
        sérénité.
      </>
    );
  }
  if (city.templateId === "chateaufort") {
    return (
      <>
        S&apos;installer ou quitter le village historique de Châteaufort,
        niché dans la haute vallée de la Chevreuse, est une étape
        importante qui demande une organisation sans faille. Pour que ce
        changement de vie se déroule dans les meilleures conditions
        possibles, le choix de vos emballages ne doit pas être laissé au
        hasard. Utiliser des cartons de récupération souvent ramollis ou
        inadaptés expose vos affaires à des risques d&apos;écrasement ou
        de déchirure lors du transport. En choisissant nos gammes de
        fournitures professionnelles, vous vous offrez la garantie de
        contenants robustes, stables et parfaitement propres, conçus pour
        protéger l&apos;intégralité de votre mobilier et de vos
        souvenirs.
      </>
    );
  }
  if (city.templateId === "bois-darcy") {
    return (
      <>
        Vous orchestrez actuellement vos préparatifs pour emménager ou
        quitter la commune dynamique de Bois-d&apos;Arcy, tout près de la
        forêt domaniale et du canal ? Pour que cette étape charnière se
        déroule sans encombre, ne négligez pas la qualité de vos
        contenants. Utiliser du matériel inadapté ou de récupération
        expose vos biens à des risques de déchirure ou d&apos;écrasement
        dans le camion de transport. En choisissant nos gammes
        professionnelles, vous offrez une protection optimale à tout
        votre mobilier et facilitez grandement la manutention, que vous
        déménagiez par vos propres moyens ou avec l&apos;aide de
        professionnels.
      </>
    );
  }
  if (city.templateId === "fontenay-le-fleury") {
    return (
      <>
        Planifier un changement de domicile {city.aCity}, que vous
        rejoigniez le dynamisme du centre-ville, le secteur de la gare ou
        les zones plus résidentielles, exige de la méthode et de la
        rigueur. Pour que cette transition se déroule sans accroc, la
        conception de vos emballages est votre premier levier de
        réussite. Oubliez les boîtes de récupération déformées qui
        compliquent le chargement et mettent vos affaires en péril. Nos
        gammes professionnelles intègrent des poignées ergonomiques
        renforcées et des formats standardisés qui s&apos;emboîtent au
        millimètre près, transformant la corvée de la manutention en une
        étape fluide, sécurisée et parfaitement maîtrisée.
      </>
    );
  }
  if (city.templateId === "les-clayes-sous-bois") {
    return (
      <>
        Planifier un changement d&apos;adresse {city.aCity}, que vous
        habitiez à proximité du centre commercial One Nation Paris, dans
        les quartiers résidentiels de la Breitche, ou près de la gare de
        Villepreux — Les Clayes, exige une organisation sans faille. Le
        dynamisme de cette commune des Yvelines impose de
        s&apos;équiper avec un matériel de transport d&apos;une
        fiabilité absolue pour éviter les contretemps le jour J. En
        choisissant nos gammes professionnelles neuves et renforcées,
        vous protégez vos biens les plus précieux (appareils high-tech,
        vaisselle fine, vêtements de marque) dans des contenants
        robustes et sains, garantissant ainsi une transition fluide vers
        votre nouveau lieu de vie.
      </>
    );
  }
  if (city.templateId === "jouy-en-josas") {
    return (
      <>
        S&apos;installer ou déménager {city.aCity}, de la tranquillité
        de la vallée de la Bièvre aux abords du campus d&apos;HEC ou du
        quartier des Metz, requiert un matériel logistique
        irréprochable. Pour que ce changement de vie se déroule dans des
        conditions optimales, la qualité de vos emballages est votre
        meilleure alliée. En choisissant nos gammes professionnelles,
        vous protégez vos biens les plus précieux (œuvres d&apos;art,
        archives, vaisselle fragile) dans des contenants robustes, sains
        et rigides, spécialement conçus pour résister aux manipulations
        et garantir un transport sans aucune fausse note.
      </>
    );
  }
  if (city.templateId === "les-loges-en-josas") {
    return (
      <>
        Planifier un changement d&apos;adresse {city.aCity}, que vous
        habitiez dans le secteur préservé du cœur de village, à
        proximité du domaine de la Cour Roland ou dans les hauteurs
        résidentielles de la commune, demande une logistique soignée. Le
        cadre verdoyant et le relief vallonné de cette commune limitrophe
        de Jouy-en-Josas imposent de s&apos;équiper avec un matériel de
        transport d&apos;une fiabilité absolue. En misant sur notre
        catalogue d&apos;emballages de qualité industrielle, vous
        protégez vos livres, vos vêtements et vos objets précieux contre
        l&apos;humidité résiduelle et les vibrations du transport. Vos
        biens voyagent ainsi dans une véritable armure rigide,
        garantissant leur arrivée intacte.
      </>
    );
  }
  if (city.templateId === "toussus-le-noble") {
    return (
      <>
        S&apos;installer ou quitter {city.name}, que vous habitiez une
        maison familiale dans le cœur paisible du village ou que vous
        pilotiez un transfert d&apos;activité à proximité de la zone
        d&apos;activités et de l&apos;aérodrome historique, exige une
        méthode sans faille. À l&apos;image de la rigueur aéronautique
        qui fait la renommée de notre commune, la préparation de vos
        colis demande les meilleurs outils. Oubliez les cartons de
        récupération fragiles et déformés. En choisissant nos emballages
        de qualité industrielle à double cannelure, vous offrez à vos
        biens une structure ultra-rigide capable de supporter le
        transport routier en toute sécurité.
      </>
    );
  }
  if (city.templateId === "plaisir") {
    return (
      <>
        Changer d&apos;adresse {city.aCity}, que vous vous installiez
        dans une maison familiale dans le quartier verdoyant de la
        Bretéchelle, un appartement moderne près du centre-ville, ou que
        vous transfériez des locaux professionnels à proximité de la
        zone commerciale des Gâtines ou de la gare de Plaisir —
        Grignon, exige une infrastructure logistique sans faille. Dans
        une commune aussi vaste et dynamique, la réussite de votre
        projet repose sur un facteur clé : la qualité de votre matériel
        d&apos;emballage. En laissant de côté les cartons de
        récupération fragiles et inadaptés, vous sécurisez vos biens
        précieux, vos équipements technologiques et vos souvenirs. Nos
        fournitures professionnelles neuves et renforcées offrent la
        structure rigide nécessaire pour affronter le transport en
        toute sérénité.
      </>
    );
  }
  if (city.templateId === "villepreux") {
    return (
      <>
        Initier un parcours de déménagement au sein de la commune
        verdoyante et résidentielle de Villepreux requiert des outils
        d&apos;emballage irréprochables. Que vous quittiez une maison
        familiale proche du Domaine de Grand&apos;Maisons, un appartement
        contemporain dans le quartier des Hauts-de-Villepreux ou le
        secteur historique du Village, préserver l&apos;intégrité de vos
        effets personnels face aux vibrations de la route et aux
        manipulations physiques est un prérequis absolu. Notre gamme de
        contenants à forte densité structurelle offre la stabilité
        nécessaire pour préserver vos collections, vos équipements et
        votre patrimoine familial tout au long de son acheminement.
      </>
    );
  }
  if (city.templateId === "chavenay") {
    return (
      <>
        Qu&apos;il s&apos;agisse de s&apos;installer au cœur du village
        préservé de {city.name}, à proximité de son aérodrome historique
        ou dans la douceur de la plaine de Versailles, un déménagement
        marque le début d&apos;une nouvelle aventure. Pour que ce moment
        charnière se déroule sans accroc, la sélection de vos contenants
        est la première décision stratégique à prendre. Oubliez les
        cartons fins ou déjà affaissés qui mettent en péril vos effets
        personnels. En misant sur du matériel neuf de qualité
        industrielle, vous protégez vos biens les plus précieux contre
        les secousses de la route et facilitez grandement le travail de
        manutention.
      </>
    );
  }
  if (city.templateId === "rennemoulin") {
    return (
      <>
        Organiser un déménagement {city.aCity}, que vous habitiez une
        ancienne bâtisse chargée d&apos;histoire près du ru de Gally, un
        corps de ferme réhabilité ou une maison nichée au calme de la
        plaine de Versailles, demande un soin tout particulier. Dans ce
        cadre de vie unique et préservé, chaque objet a une histoire et
        mérite le meilleur matériel pour voyager sans encombre. Oubliez
        les cartons fins de supermarché qui prennent l&apos;humidité et
        s&apos;effondrent. En choisissant nos emballages de qualité
        industrielle renforcés, vous offrez à votre mobilier, vos objets
        de valeur et vos souvenirs une véritable armure contre les
        vibrations du transport.
      </>
    );
  }
  if (city.templateId === "noisy-le-roi") {
    return (
      <>
        Déménager {city.aCity}, que vous quittiez une propriété à
        l&apos;orée de la forêt de Marly, une demeure à proximité de la
        Plaine de Versailles ou un appartement dans les secteurs calmes
        du centre-ville, impose un standard d&apos;organisation élevé.
        Vos biens de valeur, vos pièces de mobilier et vos souvenirs
        familiaux méritent une protection à la hauteur de leur
        importance. Oubliez définitivement les cartons de supermarché
        fins et incertains. En adoptant notre cartonnerie technique de
        qualité industrielle, vous offrez à vos effets personnels une
        structure rigide et saine, conçue pour résister à
        l&apos;écrasement vertical dans le camion de transport et
        garantir une transition d&apos;un standing irréprochable.
      </>
    );
  }
  if (city.templateId === "bailly") {
    return (
      <>
        S&apos;installer ou quitter la commune résidentielle de
        {" "}{city.name}, nichée dans la plaine de Versailles, marque le
        début d&apos;une nouvelle aventure. Pour aborder cette transition
        dans les meilleures conditions, la solidité de votre matériel
        d&apos;emballage est votre meilleure alliée. Loin des cartons de
        récupération fragiles et inadaptés, nous vous proposons des
        fournitures de qualité professionnelle. Conçus pour supporter le
        poids, résister à l&apos;humidité et s&apos;empiler parfaitement
        dans le camion de transport, nos produits vous garantissent que
        vos effets personnels arriveront intacts dans votre futur lieu
        de vie.
      </>
    );
  }
  if (city.templateId === "saint-nom-la-breteche") {
    return (
      <>
        Déménager {city.aCity}, que vous quittiez une superbe propriété
        en lisière du golf, une demeure familiale dans le Vallon ou un
        bien d&apos;exception au cœur du village, impose des standards
        d&apos;organisation élevés. Vos meubles de valeur, vos œuvres
        d&apos;art et vos souvenirs précieux méritent un matériel de
        protection à la hauteur de leur importance. Oubliez
        définitivement les cartons de récupération minces et fragiles.
        En faisant le choix de notre cartonnerie neuve de qualité
        industrielle renforcée, vous offrez à vos effets personnels une
        structure rigide et saine, spécialement conçue pour résister à
        l&apos;écrasement vertical et garantir un transport en toute
        sérénité.
      </>
    );
  }
  if (city.templateId === "davron") {
    return (
      <>
        S&apos;installer dans le cadre paisible et préservé de
        {" "}{city.name}, au cœur de la plaine de Versailles, est un
        magnifique projet qui demande une préparation minutieuse. Pour
        que ce déménagement se déroule sans le moindre stress, le choix
        de vos fournitures est l&apos;élément clé qui conditionne la
        sécurité de vos biens. Abandonner les cartons de fortune au
        profit d&apos;équipements neufs et solides vous évite les
        mauvaises surprises le jour J. Nos solutions professionnelles
        sont conçues pour offrir à vos objets précieux, vos documents et
        vos souvenirs de famille une protection irréprochable tout au
        long du transport.
      </>
    );
  }
  if (city.templateId === "poissy") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        habitiez dans un bel appartement du centre-ville historique, une
        maison résidentielle sur les hauteurs de Noailles, ou que vous
        pilotiez un transfert d&apos;activité près des pôles économiques
        majeurs de la ville, demande une préparation logistique sans
        faille. Pour que cette transition se déroule dans les meilleures
        conditions possibles, la qualité de vos emballages est
        primordiale. En abandonnant les cartons de récupération
        fragiles et aux dimensions incertaines, vous faites le choix de
        la sécurité. Nos fournitures professionnelles neuves et
        renforcées protègent vos biens contre les chocs et les aléas du
        transport, garantissant que chacun de vos objets arrive intact
        à sa nouvelle destination.
      </>
    );
  }
  if (city.templateId === "carrieres-sous-poissy") {
    return (
      <>
        Vous orchestrez actuellement votre changement de domicile ou le
        transfert de vos locaux professionnels {city.aCity}, à
        proximité des bords de Seine ou du parc du Peuple de
        l&apos;herbe ? Pour aborder cette grande étape en toute
        tranquillité, une bonne logistique est indispensable. La
        réussite de votre projet commence par le choix de contenants
        adaptés, capables de sécuriser vos biens. Loin des emballages
        de fortune souvent fragiles, nos fournitures professionnelles
        vous apportent la solidité nécessaire pour emballer, stocker et
        transporter l&apos;intégralité de vos affaires en toute
        confiance.
      </>
    );
  }
  if (city.templateId === "orgeval") {
    return (
      <>
        Changer d&apos;adresse {city.aCity}, que vous quittiez une grande
        propriété arborée sur les hauteurs, une maison familiale neuve
        ou un logement à proximité du centre historique et des accès de
        l&apos;A13, ne s&apos;improvise pas à la dernière minute. Un
        projet réussi repose avant tout sur l&apos;anticipation et sur
        le choix de matériels adaptés. Trop souvent sous-estimée, la
        phase d&apos;emballage détermine la sécurité de
        l&apos;ensemble de vos biens. En choisissant des cartons neufs
        de qualité industrielle plutôt que des emballages de
        récupération fragiles, vous vous offrez la garantie d&apos;un
        rangement sain, ordonné et hautement résistant aux contraintes
        de la manutention et du transport routier.
      </>
    );
  }
  if (city.templateId === "chambourcy") {
    return (
      <>
        Vous planifiez prochainement un déménagement {city.aCity}, près
        de son célèbre chêne séculaire, de son golf ou de ses zones
        résidentielles arborées ? Pour que ce nouveau chapitre commence
        sous les meilleurs auspices, la qualité de vos emballages est
        un élément fondamental. Délaisser les cartons de récupération
        au profit de fournitures professionnelles, c&apos;est
        s&apos;assurer que vos objets de valeur, vos vêtements et vos
        souvenirs précieux traverseront le transport sans le moindre
        dommage. Nos produits sont spécialement conçus pour offrir la
        robustesse et la stabilité nécessaires à une manutention
        totalement sécurisée.
      </>
    );
  }
  if (city.templateId === "villennes-sur-seine") {
    return (
      <>
        Quitter ou s&apos;installer {city.aCity}, que vous habitiez une
        superbe demeure en bord de Seine, une propriété sur les hauteurs
        verdoyantes ou une villa contemporaine, exige une logistique
        sans accroc. Pour protéger votre mobilier d&apos;exception, vos
        souvenirs précieux et vos œuvres d&apos;art contre les risques
        liés au transport, l&apos;utilisation d&apos;emballages de
        qualité industrielle est indispensable. Nos fournitures rigides
        et neuves forment un rempart solide, garantissant que vos objets
        franchissent cette transition en parfait état.
      </>
    );
  }
  if (city.templateId === "verneuil-sur-seine") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        quittiez une maison familiale à proximité de la base de loisirs,
        un pavillon arboré dans les hauteurs ou un appartement près de
        la gare de Vernouillet-Verneuil, demande une préparation
        méthodique. Pour que votre transition se déroule sans la moindre
        source de stress, la qualité de vos emballages est votre
        premier atout. En abandonnant les cartons de récupération
        fragiles et incertains, vous faites le choix de la sécurité.
        Nos fournitures professionnelles neuves et renforcées protègent
        vos objets de valeur, vos souvenirs et votre mobilier contre
        les aléas de la manutention, garantissant une arrivée à
        destination en toute sérénité.
      </>
    );
  }
  if (city.templateId === "vernouillet") {
    return (
      <>
        Planifier un déménagement {city.aCity}, que vous vous installiez
        dans un pavillon arboré du quartier du Marsin, une maison proche
        du centre historique ou un appartement familial, demande une
        excellente préparation logistique. Pour aborder ce changement
        de vie sans stress, la qualité de vos emballages reste votre
        meilleure garantie de succès.
        <br />
        <br />
        En choisissant nos fournitures, vous protégez efficacement vos
        biens. Nos modèles renforcés offrent la rigidité nécessaire pour
        supporter l&apos;empilement et sécuriser vos souvenirs tout au
        long du transport.
      </>
    );
  }
  if (city.templateId === "morainvilliers") {
    return (
      <>
        Préparer un déménagement {city.aCity}, que vous habitiez dans
        le cœur du village, dans le secteur recherché de Bures ou à
        proximité des accès de l&apos;A13, demande une organisation
        sans faille pour préserver votre confort. Pour aborder cette
        transition en toute confiance, la qualité de vos fournitures est
        votre meilleur allié. Oubliez la corvée et les risques liés aux
        cartons de récupération fragiles et poussiéreux. En choisissant
        nos gammes professionnelles renforcées, vous offrez à vos
        vêtements, vaisselles, objets d&apos;art et souvenirs une
        protection optimale. Vos effets personnels voyagent ainsi dans
        des structures saines et rigides, spécialement conçues pour
        supporter le transport en Île-de-France.
      </>
    );
  }
  if (city.templateId === "maisons-laffitte") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        résidiez dans les somptueuses avenues arborées du Parc, à
        proximité immédiate du Château, ou dans le centre-ville
        dynamique près de la gare, requiert une logistique
        d&apos;exception. Dans une ville reconnue pour son élégance et
        ses propriétés de prestige, la préservation de votre patrimoine
        familial et de vos objets de valeur est une priorité absolue. En
        faisant le choix de nos fournitures neuves de qualité
        industrielle, vous offrez à vos biens une protection haut de
        gamme. Nos cartons robustes et rigides sont spécialement conçus
        pour résister aux manipulations exigeantes et s&apos;empiler en
        toute stabilité, vous garantissant un départ en toute sérénité.
      </>
    );
  }
  if (city.templateId === "le-mesnil-le-roi") {
    return (
      <>
        Préparer un changement d&apos;adresse {city.aCity}, que vous
        résidiez dans le calme du quartier de la Prairie, à proximité
        du château ou sur les hauteurs en lisière de la forêt de
        Saint-Germain-en-Laye, exige un matériel à la hauteur de vos
        exigences. Pour que cette transition se déroule sous le signe
        de la sérénité, la qualité de vos contenants est primordiale.
        En choisissant nos gammes professionnelles, vous protégez vos
        effets personnels, vos livres et vos objets précieux dans des
        cartons rigides, neufs et sains. C&apos;est la garantie
        d&apos;éviter les mauvaises surprises des boîtes de
        récupération qui cèdent au moindre levage, assurant un
        transport sans accroc vers votre nouveau foyer.
      </>
    );
  }
  if (city.templateId === "acheres") {
    return (
      <>
        Vous préparez votre déménagement {city.aCity}, près de la forêt
        de Saint-Germain-en-Laye ou du quartier de la gare ? Pour
        emballer vos biens en toute sécurité, ne laissez rien au
        hasard. Notre boutique en ligne vous propose une large gamme de
        cartons de déménagement professionnels et d&apos;accessoires
        d&apos;emballage de haute qualité, livrés directement à votre
        domicile ou dans vos locaux professionnels {city.aCity}.
        <br />
        <br />
        Le saviez-vous ? Un bon déménagement commence par un bon
        équipement. Finis les cartons de récupération qui craquent sous
        le poids de vos livres : optez pour la solidité et la sérénité.
      </>
    );
  }
  if (city.templateId === "conflans-sainte-honorine") {
    return (
      <>
        Planifier un changement de domicile ou le transfert de vos
        locaux {city.aCity}, qu&apos;il s&apos;agisse de
        s&apos;installer sur les hauteurs du Vieux Conflans ou à
        proximité du port, est une étape majeure. Pour que cette
        transition se déroule dans les meilleures conditions possibles,
        le choix de vos emballages ne doit pas être laissé au hasard.
        Vos objets du quotidien, vos souvenirs de famille et vos
        équipements de valeur méritent une protection d&apos;un niveau
        supérieur. En choisissant nos gammes professionnelles, vous
        dites adieu aux cartons de récupération incertains pour offrir
        à vos biens un environnement robuste, propre et parfaitement
        sécurisé.
      </>
    );
  }
  if (city.templateId === "andresy") {
    return (
      <>
        Changer de vie ou de locaux {city.aCity}, entre les boucles de
        la Seine et le charme de l&apos;Île de Nancy, demande une
        sacrée organisation. Pour que cette transition se fasse sans
        stress, le secret réside dans le choix de vos contenants.
        Oubliez la course aux cartons de fortune récupérés à droite à
        gauche, souvent trop fins ou humides. Nous mettons à votre
        disposition des emballages robustes et professionnels,
        spécialement conçus pour affronter le transport. C&apos;est
        l&apos;assurance que votre vaisselle, vos vêtements et vos
        objets les plus précieux voyageront dans des conditions
        optimales d&apos;Andrésy jusqu&apos;à votre nouvelle adresse.
      </>
    );
  }
  if (city.templateId === "chanteloup-les-vignes") {
    return (
      <>
        Prendre racine ou écrire un nouveau chapitre de sa vie
        {" "}{city.aCity}, sur les hauteurs verdoyantes dominant la
        vallée de la Seine, est un projet qui insuffle un bel élan.
        Pour que ce déménagement se transforme en une expérience
        positive, la préparation de vos colis est l&apos;étape
        essentielle sur laquelle repose toute votre organisation. Vos
        objets du quotidien, vos souvenirs et vos pièces de valeur
        méritent des écrins à la hauteur de leur importance. En
        choisissant nos gammes d&apos;emballages professionnels, vous
        quittez les approximations des cartons de récupération pour
        offrir à vos biens la certitude d&apos;un voyage totalement
        sécurisé et sans stress.
      </>
    );
  }
  if (city.templateId === "maurecourt") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        habitiez dans le centre historique près des bords de
        l&apos;Oise, à proximité de la gare ou dans les zones
        résidentielles plus récentes, demande une vraie méthode. Pour
        éviter le stress des préparatifs, l&apos;utilisation
        d&apos;emballages professionnels neufs et robustes est
        indispensable. Ne prenez pas le risque de voir vos affaires
        s&apos;abîmer dans des boîtes de récupération fragiles. Nos
        cartons à double cannelure de qualité industrielle offrent une
        barrière étanche et solide pour protéger vos objets du
        quotidien, vos livres et vos équipements multimédias tout au
        long de leur transport sur les routes des Yvelines.
      </>
    );
  }
  if (city.templateId === "triel-sur-seine") {
    return (
      <>
        Changer de rive ou de quartier {city.aCity}, que vous
        emménagiez dans une maison de caractère sur les coteaux de
        l&apos;Hautil, un appartement en bord de Seine ou que vous
        transfériez des locaux professionnels, exige des fournitures à
        la hauteur de l&apos;événement. Pour naviguer dans cette
        transition sans stress, oubliez les cartons fragiles de
        récupération. Nos emballages professionnels renforcés
        garantissent à vos effets personnels une protection absolue
        contre les aléas de la manutention et du transport routier.
      </>
    );
  }
  if (city.templateId === "chatou") {
    return (
      <>
        Planifier un changement de résidence ou le transfert de ses
        bureaux {city.aCity}, la prestigieuse cité des impressionnistes
        idéalement située le long de la Seine, est un projet
        d&apos;envergure qui exige une organisation sans faille. Pour
        que cette transition majeure se déroule sous les meilleurs
        auspices, la sélection de vos contenants est le premier pilier
        de votre réussite logistique. Délaisser les cartons de fortune
        au profit d&apos;équipements de qualité industrielle est le
        seul moyen de garantir l&apos;intégrité de vos biens lors du
        transport. Notre gamme exclusive offre la robustesse et la
        propreté nécessaires pour que vos objets d&apos;art, votre
        mobilier précieux et l&apos;ensemble de vos souvenirs
        traversent les manipulations sans le moindre dommage.
      </>
    );
  }
  if (city.templateId === "le-vesinet") {
    return (
      <>
        Organiser un déménagement {city.aCity}, que vous quittiez une
        demeure historique du quartier des Princes, une propriété en
        lisière des pelouses d&apos;Ibis, ou un appartement à proximité
        de la gare du Vésinet-Centre, exige un soin absolu. Dans cette
        ville-parc unique aux allées arborées et aux demeures de
        prestige, la protection de votre patrimoine est notre
        priorité majeure. Utiliser des emballages de qualité
        industrielle neufs et ultra-robustes est la clé pour aborder
        votre changement d&apos;adresse avec une sérénité totale, en
        mettant vos œuvres d&apos;art, livres, mobiliers et souvenirs
        à l&apos;abri des aléas du transport.
      </>
    );
  }
  if (city.templateId === "croissy-sur-seine") {
    return (
      <>
        Changer d&apos;adresse {city.aCity}, que ce soit pour vous
        rapprocher des berges de la Seine très chères aux
        impressionnistes ou pour vous installer dans le cœur
        historique de la ville, est une étape qui mérite une
        préparation d&apos;excellence. Pour aborder cette transition
        l&apos;esprit léger, l&apos;organisation de vos colis est le
        premier paramètre à maîtriser. Délaisser le matériel de
        récupération au profit de fournitures neuves et calibrées est
        le meilleur moyen de mettre vos biens à l&apos;abri des
        imprévus. Nos solutions d&apos;emballage vous apportent la
        structure et la propreté nécessaires pour entamer votre
        déménagement sous les meilleurs auspices.
      </>
    );
  }
  if (city.templateId === "bougival") {
    return (
      <>
        Vous préparez votre projet de déménagement {city.aCity}, la
        célèbre ville des impressionnistes en bord de Seine ?
        Qu&apos;il s&apos;agisse de vous installer sur les hauteurs
        de la colline ou de quitter les quais, l&apos;organisation de
        vos cartons est l&apos;étape maîtresse de votre transition.
        Pour éviter le stress des objets brisés ou des cartons de
        fortune qui s&apos;effondrent, choisir des équipements de
        qualité industrielle est indispensable. Nous mettons à votre
        disposition des fournitures robustes qui s&apos;empilent
        parfaitement, garantissant un transport serein et sécurisé
        pour l&apos;ensemble de votre mobilier et de vos souvenirs.
      </>
    );
  }
  if (city.templateId === "la-celle-saint-cloud") {
    return (
      <>
        Organiser un déménagement {city.aCity}, que vous quittiez le
        secteur verdoyant de la Feuilleraie, le quartier recherché de
        la Châtaigneraie, ou les résidences arborées d&apos;Elysée,
        demande une logistique soignée. Pour que ce changement de vie
        se déroule dans des conditions parfaites, la qualité de vos
        contenants est primordiale. En choisissant nos fournitures
        neuves de qualité industrielle, vous offrez à vos biens la
        protection qu&apos;ils méritent. Vos objets d&apos;art, vos
        livres et vos effets personnels sont ceinturés dans un
        carton rigide et sain, conçu pour résister aux manipulations
        et s&apos;empiler en toute sécurité dans le camion.
      </>
    );
  }
  if (city.templateId === "louveciennes") {
    return (
      <>
        Organiser un déménagement {city.aCity}, que vous quittiez une
        propriété sur les hauteurs du quartier des Plains, une demeure
        proche du Château de Madame du Barry, ou une résidence
        arborée à proximité de la gare, demande une attention toute
        particulière. Cette ville d&apos;art et d&apos;histoire, qui
        a inspiré les plus grands peintres impressionnistes, abrite
        des patrimoines précieux qui méritent une protection
        irréprochable. En choisissant nos fournitures neuves de
        qualité industrielle, vous éliminez les risques liés au
        transport. Vos tableaux, sculptures, livres rares et objets
        du quotidien sont protégés par des cartons à double
        cannelure rigides et sains, capables de résister aux
        manipulations les plus exigeantes.
      </>
    );
  }
  if (city.templateId === "marly-le-roi") {
    return (
      <>
        Planifier un déménagement {city.aCity}, que vous quittiez le
        charme authentique du Vieux-Marly, une demeure à proximité du
        Parc Royal, ou un appartement dans les secteurs résidentiels
        des Grandes Terres, requiert une logistique irréprochable.
        Dans cette ville chargée d&apos;histoire et d&apos;élégance,
        la protection de vos biens personnels est notre priorité
        absolue. En faisant le choix de nos fournitures de qualité
        industrielle neuves et renforcées, vous mettez vos œuvres
        d&apos;art, livres, mobiliers et souvenirs à l&apos;abri des
        aléas du transport. Vos effets voyagent ainsi dans une
        véritable armure rigide, vous garantissant un départ vers
        votre nouveau lieu de vie en toute sérénité.
      </>
    );
  }
  if (city.templateId === "le-port-marly") {
    return (
      <>
        S&apos;installer ou déménager {city.aCity}, que vous résidiez
        sur les quais de Seine, dans le quartier historique ou à
        proximité du domaine des Lions, demande une organisation
        logistique sans faille. La spécificité des accès et le rythme
        des bords de Seine imposent d&apos;utiliser un matériel de
        transport d&apos;une fiabilité absolue pour sécuriser vos
        biens. En choisissant nos gammes professionnelles neuves et
        renforcées, vous protégez vos objets précieux, vos meubles
        de valeur et vos souvenirs contre l&apos;humidité et les
        vibrations de la route, garantissant ainsi une transition
        en toute sérénité.
      </>
    );
  }
  if (city.templateId === "houilles") {
    return (
      <>
        Changer de vie {city.aCity}, que vous quittiez le quartier
        très recherché de la Gare, l&apos;animation du Centre-Ville,
        ou le calme résidentiel des Blanches ou de Maintenon, est une
        étape majeure qui mérite un matériel de premier choix. Dans
        une ville aussi dynamique et connectée, chaque aspect de
        votre logistique doit être parfaitement huilé pour éviter le
        stress du jour J. Entamer vos cartons avec des fournitures
        professionnelles neuves est la garantie absolue de protéger
        votre patrimoine contre les vibrations et les manipulations
        répétées. Nos emballages haute résistance sont conçus pour
        structurer vos volumes, optimiser l&apos;espace dans les
        camions et vous offrir la sérénité que vous êtes en droit
        d&apos;attendre pour votre installation.
      </>
    );
  }
  if (city.templateId === "carrieres-sur-seine") {
    return (
      <>
        Vous préparez vos cartons pour un emménagement ou un départ
        de la charmante ville {city.aCity}, entre ses quartiers
        historiques peints par les impressionnistes et ses zones
        résidentielles prisées ? Pour aborder cette transition
        importante dans les meilleures conditions, la qualité de vos
        emballages est votre premier gage de réussite. En choisissant
        nos gammes de fournitures professionnelles, vous protégez vos
        effets personnels contre les aléas du transport et de la
        manutention. Solides et faciles à empiler, nos cartons vous
        garantissent une organisation sans faille pour déménager
        sereinement.
      </>
    );
  }
  if (city.templateId === "montesson") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        habitiez dans le quartier historique du Centre-ville, dans les
        zones résidentielles calmes des Rabrets, ou à proximité de la
        plaine maraîchère, demande une organisation sans faille. Pour
        aborder cette transition en toute sérénité, la qualité de
        vos emballages est primordiale. En choisissant nos gammes
        professionnelles renforcées à double cannelure, vous protégez
        efficacement vos effets personnels contre les aléas de la
        manutention. Vos souvenirs, vos appareils connectés et vos
        documents précieux sont ceinturés dans des structures saines
        et rigides, spécialement conçues pour supporter le transport
        sur les routes d&apos;Île-de-France sans aucune déformation.
      </>
    );
  }
  if (city.templateId === "saint-germain-en-laye") {
    return (
      <>
        Planifier un déménagement {city.aCity}, que vous quittiez un
        hôtel particulier du centre historique proche du Château, une
        propriété résidentielle dans le quartier de l&apos;Ermitage,
        ou un appartement familial près du Lycée International, exige
        une organisation d&apos;un standing irréprochable. Vos biens
        précieux, vos collections et vos souvenirs méritent une
        protection à la hauteur de leur valeur. Oubliez définitivement
        les cartons de récupération minces et incertains. En
        choisissant notre cartonnerie de qualité industrielle
        renforcée, vous offrez à vos effets personnels une structure
        saine et hautement résistante, conçue pour supporter
        l&apos;écrasement vertical dans le camion et garantir une
        transition en toute sérénité.
      </>
    );
  }
  if (city.templateId === "le-pecq") {
    return (
      <>
        Planifier un déménagement {city.aCity}, que vous logiez sur la
        rive gauche près du centre-ville, sur la rive droite dans le
        quartier recherché du Mexique, ou sur les hauteurs de
        Saint-Wandrille, impose une logistique parfaitement maîtrisée.
        L&apos;accès aux étages et la configuration des rues alpines
        de la commune demandent un matériel de transport d&apos;une
        fiabilité absolue pour éviter tout stress inutile.
        <br />
        <br />
        En choisissant nos emballages de qualité industrielle, vous
        donnez à vos biens la meilleure protection possible. Oubliez
        les cartons de récupération fragiles qui menacent de
        s&apos;effondrer. Nos fournitures neuves et renforcées
        maintiennent vos objets de valeur, votre matériel high-tech
        et vos souvenirs à l&apos;abri des chocs, assurant un
        chargement stable et serein dans le camion de transport.
      </>
    );
  }
  if (city.templateId === "mareil-marly") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        résidiez dans le charme historique du cœur de village, à
        proximité de la gare de la Lisière en lisière de forêt, ou
        dans les secteurs résidentiels des Hauteurs, exige un
        matériel irréprochable. Le relief vallonné et les accès
        parfois étroits de cette commune préservée imposent
        d&apos;utiliser des contenants d&apos;une robustesse absolue.
        En choisissant nos gammes professionnelles à double cannelure,
        vous offrez une protection haut de gamme à vos effets
        personnels. Vos souvenirs, livres et objets d&apos;art voyagent
        dans des structures rigides et saines, éliminant tout risque
        d&apos;affaissement ou de déchirure lors du transport.
      </>
    );
  }
  if (city.templateId === "letang-la-ville") {
    return (
      <>
        Planifier un changement d&apos;adresse {city.aCity}, que vous
        habitiez près de la forêt de Marly, dans le secteur de la
        gare de Saint-Nom-la-Bretèche ou dans les hauteurs
        résidentielles de la commune, demande une organisation
        méthodique. Pour que votre transition se déroule dans un
        calme absolu, la robustesse de vos cartons est votre
        meilleure assurance. En choisissant nos fournitures neuves
        de qualité industrielle, vous protégez vos meubles de valeur,
        vos souvenirs et vos objets fragiles contre l&apos;humidité
        et les vibrations du transport. Vous abordez ainsi votre
        déménagement avec la certitude que chaque boîte arrivera
        intacte dans votre nouvelle demeure.
      </>
    );
  }
  if (city.templateId === "feucherolles") {
    return (
      <>
        S&apos;installer ou déménager dans la charmante commune
        {" "}{city.aCity}, au cœur de la plaine de Versailles, exige
        une logistique impeccable pour préserver la sérénité de votre
        projet. Pour réussir cette transition en toute confiance,
        disposer d&apos;un partenaire local pour vos emballages est
        un atout stratégique majeur. Finis les cartons récupérés à
        droite à gauche, souvent fragiles ou inadaptés, qui mettent
        en péril vos effets personnels. En optant pour nos solutions
        de proximité, vous accédez à un matériel robuste, propre et
        parfaitement calibré pour sécuriser l&apos;intégralité de
        votre patrimoine.
      </>
    );
  }
  if (city.templateId === "sartrouville") {
    return (
      <>
        Organiser un changement d&apos;adresse {city.aCity}, que vous
        quittiez un pavillon familial dans le quartier recherché de
        la Plaine, un appartement moderne près de la gare ou du
        centre-ville, ou que vous transfériez des locaux
        professionnels à proximité des zones d&apos;activités, exige
        une organisation rigoureuse. Dans une ville aussi vaste et
        dynamique, la qualité de vos fournitures est le premier
        pilier de votre réussite. En laissant de côté les cartons de
        récupération fragiles, minces et incertains, vous sécurisez
        vos biens précieux, vos équipements technologiques et vos
        souvenirs. Nos gammes professionnelles neuves et renforcées
        offrent la structure rigide nécessaire pour affronter la
        manutention et le transport en toute sérénité.
      </>
    );
  }
  if (city.templateId === "aigremont") {
    return (
      <>
        Vous préparez votre déménagement depuis ou vers la paisible
        commune {city.aCity} ? Qu&apos;il s&apos;agisse de quitter
        une belle maison familiale ou de vous installer dans ce cadre
        privilégié des Yvelines, la réussite de votre projet commence
        par un emballage irréprochable. Notre boutique en ligne vous
        propose une sélection complète de cartons de déménagement
        professionnels et de matériel de protection, livrés
        directement à votre porte {city.aCity}.
      </>
    );
  }
  if (city.templateId === "andelu") {
    return (
      <>
        Vous planifiez prochainement un déménagement depuis ou vers
        la charmante commune rurale {city.aCity} ? Qu&apos;il
        s&apos;agisse d&apos;un changement de domicile ou du
        transfert de vos locaux professionnels dans le cœur des
        Yvelines, la réussite de votre projet repose d&apos;abord sur
        la qualité de votre emballage. Notre boutique en ligne met à
        votre disposition une sélection rigoureuse de cartons
        professionnels et d&apos;accessoires de protection haute
        résistance. En évitant les cartons de récupération souvent
        fragilisés, vous offrez à vos biens de valeur, à vos livres
        et à vos souvenirs la certitude d&apos;arriver à destination
        en parfait état.
      </>
    );
  }
  if (city.templateId === "bazemont") {
    return (
      <>
        Changer de lieu de vie ou transférer ses bureaux {city.aCity},
        ce charmant village perché des Yvelines, est un projet
        stimulant qui mérite une logistique irréprochable. Pour
        éviter les mauvaises surprises le jour du départ, la qualité
        de vos contenants est primordiale. Oubliez définitivement les
        cartons de fortune récupérés dans les commerces, souvent
        fragiles ou abîmés. Nous mettons à votre disposition des
        boîtes de déménagement d&apos;une grande robustesse, conçues
        pour préserver l&apos;intégrité de vos biens, de vos objets
        d&apos;art et de vos souvenirs tout au long de leur
        acheminement.
      </>
    );
  }
  if (city.templateId === "crespieres") {
    return (
      <>
        Planifier un changement de domicile ou l&apos;installation
        de vos locaux {city.aCity}, au cœur de la plaine de
        Versailles et de la vallée du Ru de Gally, est un projet
        stimulant qui demande une logistique rigoureuse. Pour que
        cette transition se déroule dans les meilleures conditions
        possibles, la qualité de vos contenants est votre premier
        rempart contre les mauvaises surprises. Utiliser des cartons
        inadaptés ou de récupération expose vos biens à des risques
        de casse ou d&apos;affaissement. En choisissant nos gammes
        professionnelles, vous sécurisez chaque étape de vos
        préparatifs et vous assurez que vos souvenirs, votre
        mobilier et vos objets précieux voyageront en toute
        sécurité.
      </>
    );
  }
  if (city.templateId === "herbeville") {
    return (
      <>
        S&apos;installer ou déménager dans le cadre privilégié
        {" "}{city.aCity}, village de caractère niché sur les
        hauteurs de la vallée de la Mauldre, demande une
        organisation rigoureuse. Pour préserver le calme de cette
        transition, la qualité de vos emballages est le tout premier
        paramètre à maîtriser avant l&apos;arrivée des camions.
        <br />
        <br />
        En choisissant nos fournitures de qualité industrielle, vous
        quittez définitivement le monde de la récupération pour
        entrer dans celui de la haute protection. Nos solutions
        d&apos;emballage neuves et saines garantissent une étanchéité
        parfaite et une structure indéformable, idéales pour mettre
        vos biens les plus précieux à l&apos;abri des imprévus de
        la route.
      </>
    );
  }
  if (city.templateId === "les-alluets-le-roi") {
    return (
      <>
        Changer de domicile {city.aCity}, au cœur de ce charmant
        village préservé de la plaine de Versailles, demande une
        organisation rigoureuse. Loin du tumulte des grandes
        agglomérations, la réussite de votre déménagement repose sur
        un facteur clé : la fiabilité de votre matériel
        d&apos;emballage. En faisant le choix de nos gammes
        professionnelles, vous vous offrez la tranquillité
        d&apos;esprit. Vos meubles de valeur, vos souvenirs de
        famille et vos objets du quotidien sont ceinturés dans des
        contenants solides et sains, conçus pour supporter le
        transport sur les routes des Yvelines sans aucune fausse
        note.
      </>
    );
  }
  if (city.templateId === "mareil-sur-mauldre") {
    return (
      <>
        Changer de domicile {city.aCity}, que vous quittiez le cœur
        historique du village, une maison en bordure de la Mauldre
        ou une propriété sur les hauteurs de la commune, demande une
        méthodologie rigoureuse. L&apos;erreur la plus fréquente
        consiste à utiliser des cartons de récupération aux tailles
        disparates qui compliquent le chargement. En faisant le
        choix de fournitures professionnelles standardisées et de
        qualité industrielle, vous simplifiez instantanément vos
        préparatifs. Nos cartons renforcés s&apos;assemblent au
        millimètre près et s&apos;empilent parfaitement dans le
        véhicule de transport. Vos objets précieux et vos souvenirs
        sont ceinturés dans des structures saines et ultra-robustes,
        garantissant un transfert sans stress et parfaitement
        ordonné.
      </>
    );
  }
  return null;
};

const YvelinesCityPage = ({ citySlugOverride }) => {
  const params = useParams();
  const citySlug = citySlugOverride || params.citySlug;
  const city = getYvelinesCityPageBySlug(citySlug);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProducts()
      .then((res) => setProducts(res || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useDocumentTitle(
    city
      ? `Carton déménagement ${city.name} (${city.deptCode}) — Livraison rapide | cartonmove`
      : "Ville introuvable — cartonmove",
    city
      ? `Cartons et fournitures de déménagement livrés rapidement à ${city.name}. Service local, propre flotte, accessoires de protection et de fermeture.`
      : undefined
  );

  if (!city) return <Navigate to="/cartons-demenagement/yvelines" replace />;

  const template = getYvelinesCityTemplate(city.templateId);
  const featured = FEATURED_SLUGS
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean);

  // Pyramide SEO : satellite → remonte au phare parent. Phare → remonte au département.
  const parentPhare = city.parentPhareSlug
    ? getYvelinesCityPageBySlug(city.parentPhareSlug)
    : null;
  const breadcrumb = parentPhare
    ? {
        label: `Retour à ${parentPhare.name}`,
        href: `/carton-demenagement-${parentPhare.slug}`,
      }
    : {
        label: `Retour à ${city.deptName} (${city.deptCode})`,
        href: `/cartons-demenagement/${city.deptSlug}`,
      };

  return (
    <div data-testid="yvelines-city-page" data-city-slug={city.slug}>
      <JsonLd id="ld-yv-city-service" data={buildCityServiceSchema(city)} />
      {/* Breadcrumb pyramidal SEO */}
      <div className="container-cdm pt-5">
        <Link
          to={breadcrumb.href}
          className="inline-flex items-center gap-2 text-sm text-[#5b6b7a] hover:text-[#0f1d2b]"
          data-testid="yvelines-city-back-link"
        >
          <ArrowRight className="h-4 w-4 rotate-180" /> {breadcrumb.label}
        </Link>
      </div>

      {/* ======= SECTION 1 — Hero + 4 produits ======= */}
      <section className="pt-10 pb-16 md:pt-14 md:pb-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EEF3F7] border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <MapPin className="h-3.5 w-3.5" strokeWidth={2.6} />
              {city.name} · {city.deptName} ({city.deptCode})
            </div>
            <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px] text-[#0f1d2b] leading-tight">
              {buildH1(city)}
            </h1>
            <p className="mt-3 text-[14px] text-[#5b6b7a]">
              Livraison rapide {city.aCity || `à ${city.name}`} et dans tout le {city.deptCode}
              {" "}avec notre propre flotte.
            </p>
          </div>

          {/* 4 featured products */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div
              className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
              data-testid="yvelines-city-featured-products"
            >
              {featured.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
              ))}
            </div>
          )}

          <p className="mt-10 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
            <Intro city={city} />
          </p>

          <Link
            to="/cartons-demenagement"
            className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors [-webkit-tap-highlight-color:transparent]"
            data-testid="yvelines-city-cta-shop-top"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </section>

      {/* Sections SEO spécifiques au template ville */}
      {template?.Sections && <template.Sections city={city} products={products} />}
    </div>
  );
};

export default YvelinesCityPage;
